import { useCallback, useEffect } from "react"
import PropTypes from "prop-types"
import { map, size, compact, chain, find } from "lodash"
import moment from "moment"
import { Paper, Tabs, Tab } from "@mui/material"
import ExcelDownload from "components/excel-download"
import ScanSummary from "./components/scan-summary"
import CoverageReport from "./components/coverage-report"
import TagCoverageReport from "./components/tag-coverage-report"
import AttributeCoverageReport from "./components/attribute-coverage-report"
import UnmatchedSecurities from "./components/unmatched-securities"
import UntaggedSecurities from "./components/untagged-securities"
import { getExcelValue } from "helpers/csv"
import withNavigate from "hocs/withNavigate"

import "./style.scss"

const DATETIME_FORMAT = "YYYY-MM-DD-h:mm:ss-a"

function UploadScanPreview({
  navigate,
  scanResult,
  resetScan,
  uploadStep,
  toggleTab,
  activeTab,
  summary,
  coverage,
  coveragePage,
  showCoveragePage,
  numberOfTableRows,
  target,
  securities,
  securitiesPage,
  showSecuritiesPage,
  showCoverageDate,
  coverageDate,
  coverageSecurities,
  coverageSecuritiesPage,
  showCoverageSecuritiesPage,
  resetCoverageDate,
  uploadOption,
  targetName,
  uploadDateTime,
  changeUploadOption,
  openDialog,
  overlappingDates,
  type = "portfolio",
  showOverrideOptions,
  tagRows,
  attributeRows,
  untaggedSecurities,
}) {
  useEffect(() => {
    let navigateTimeout
    if (!scanResult) {
      navigateTimeout = setTimeout(() => navigate(uploadStep.url, { replace: true }))
      resetScan()
    }
    return () => {
      clearTimeout(navigateTimeout)
      resetScan()
    }
  }, [scanResult, resetScan, navigate, uploadStep.url])

  function onTabChanged(ev, value) {
    toggleTab(value)
  }

  const formattedUploadDateTime = useCallback(() => {
    return moment(uploadDateTime || Date.now()).format(DATETIME_FORMAT)
  }, [uploadDateTime])

  function downloadSecurities() {
    let csvContent

    if (type === "watchlist") {
      csvContent = [[
        "Identifier",
        "Asset Class",
      ], ...map(scanResult.scan.security, item => ([
        item.ticker || item.sedol,
        item.assetClass,
      ]))]
    } else {
      csvContent = [[
        "Identifier",
        "From",
        "To",
        "Max % GMV",
        "Counts",
      ], ...map(scanResult.scan.security, item => ([
        item.ticker || item.sedol,
        item.from,
        item.to,
        getExcelValue(item.maxGMV / 100, { percent: true }),
        item.counts,
      ]))]
    }

    return {
      fileName: `${targetName} unique unmapped securities ${formattedUploadDateTime()}`,
      data: csvContent,
      type,
    }
  }

  function downloadCoverageReport() {
    const csvContent = [[
      "Date",
      "% modeled",
      "% unmodeled",
      "% unmapped",
      "Unmapped equities",
    ], ...chain(scanResult)
      .get("scan.dates")
      .map(item => ([
        item.date,
        getExcelValue(item.percentModeled / 100, { percent: true }),
        getExcelValue((100 - item.percentModeled) / 100, { percent: true }),
        getExcelValue(item.percentUnmappedEquities / 100, { percent: true }),
        item.unmappedEquities,
      ]))
      .value(),
    ]

    return {
      fileName: `${targetName} coverage report-${formattedUploadDateTime()}`,
      data: csvContent,
      type,
    }
  }

  const nbUntaggedSecurities = size(untaggedSecurities)

  const tabs = compact([
    { value: "summary", label: "Scan Summary" },
    (type !== "watchlist" && coverage) && { value: "coverage", label: `Coverage Report: ${coverage.numberOfDates} days` },
    securities.numberOfSecurities > 0 && { value: "unmatched", label: `Unique Unmatched Securities: ${securities.numberOfSecurities}` },
    size(tagRows) > 0 && { value: "tagcoverage", label: "Tag Category Coverage Report" },
    size(attributeRows) > 0 && { value: "attributecoverage", label: "Attr. Category Coverage Report" },
    nbUntaggedSecurities > 0 && { value: "untagged", label: `Untagged Securities: ${nbUntaggedSecurities}` },
  ])

  const adjustedActiveTab = find(tabs, { value: activeTab }) ? activeTab : "summary"

  return <div className="scan-preview-wrapper">
    <Paper className="scan-preview" square elevation={0}>
      <Tabs value={adjustedActiveTab} onChange={onTabChanged} className="preview-type" indicatorColor="primary" textColor="primary">
        {map(tabs, tab => <Tab key={tab.value} {...tab} classes={{ selected: "selected" }} />)}
      </Tabs>
      {activeTab === "summary"
        && <ScanSummary
          targetName={targetName}
          openDialog={openDialog}
          overlappingDates={overlappingDates}
          showOverrideOptions={showOverrideOptions}
          summary={summary}
          uploadOption={uploadOption}
          changeUploadOption={changeUploadOption}
          type={type}
        />}
      {type !== "watchlist" && activeTab === "coverage" && coverage
        && <CoverageReport
          model={target.model}
          coverage={coverage}
          coveragePage={coveragePage}
          showCoveragePage={showCoveragePage}
          numberOfTableRows={numberOfTableRows}
          showDate={showCoverageDate}
          coverageDate={coverageDate}
          securities={coverageSecurities}
          securitiesPage={coverageSecuritiesPage}
          showSecuritiesPage={showCoverageSecuritiesPage}
          resetDate={resetCoverageDate}
          downloadButton={<ExcelDownload {...downloadCoverageReport()} />}
          type={type}
        />}
      {activeTab === "unmatched"
        && <UnmatchedSecurities
          downloadButton={<ExcelDownload {...downloadSecurities()} />}
          securities={securities}
          securitiesPage={securitiesPage}
          showSecuritiesPage={showSecuritiesPage}
          numberOfTableRows={numberOfTableRows}
          type={type}
        />}
      {activeTab === "tagcoverage"
        && <TagCoverageReport
          rows={tagRows}
          uploadDate={formattedUploadDateTime()}
          portfolioAlias={targetName}
          rowsPerPage={numberOfTableRows}
        />}
      {activeTab === "attributecoverage"
        && <AttributeCoverageReport
          rows={attributeRows}
          uploadDate={formattedUploadDateTime()}
          portfolioAlias={targetName}
          rowsPerPage={numberOfTableRows}
        />}
      {activeTab === "untagged"
        && <UntaggedSecurities
          portfolioAlias={targetName}
          uploadDate={formattedUploadDateTime()}
          securities={untaggedSecurities}
          rowsPerPage={numberOfTableRows}
        />}
    </Paper>
  </div>
}

UploadScanPreview.propTypes = {
  navigate: PropTypes.func.isRequired,
  scanResult: PropTypes.object,
  resetScan: PropTypes.func.isRequired,
  uploadStep: PropTypes.object.isRequired,
  toggleTab: PropTypes.func.isRequired,
  activeTab: PropTypes.string.isRequired,
  summary: PropTypes.object.isRequired,
  coverage: PropTypes.object,
  coveragePage: PropTypes.number.isRequired,
  showCoveragePage: PropTypes.func.isRequired,
  numberOfTableRows: PropTypes.number.isRequired,
  target: PropTypes.object.isRequired,
  securities: PropTypes.object.isRequired,
  securitiesPage: PropTypes.number.isRequired,
  showSecuritiesPage: PropTypes.func.isRequired,
  showCoverageDate: PropTypes.func.isRequired,
  coverageDate: PropTypes.string,
  coverageSecurities: PropTypes.object.isRequired,
  coverageSecuritiesPage: PropTypes.number.isRequired,
  showCoverageSecuritiesPage: PropTypes.func.isRequired,
  resetCoverageDate: PropTypes.func.isRequired,
  uploadOption: PropTypes.string,
  targetName: PropTypes.string.isRequired,
  uploadDateTime: PropTypes.object,
  changeUploadOption: PropTypes.func.isRequired,
  openDialog: PropTypes.func.isRequired,
  overlappingDates: PropTypes.array,
  type: PropTypes.string.isRequired,
  showOverrideOptions: PropTypes.bool,
  tagRows: PropTypes.array,
  attributeRows: PropTypes.array,
  untaggedSecurities: PropTypes.array,
}

export default withNavigate(UploadScanPreview)
